import { Checkbox, Button, Field, Input, Textarea, makeStyles, Text, Combobox, Option, RadioGroup, Radio } from "@fluentui/react-components";
import { Turnstile } from '@marsidev/react-turnstile'
import * as React from "react";
import { flexColumn, formStyle, intro } from "../../styles/GlobalStyles";
import { useNavigate } from "react-router-dom";
import { useGlobalDispatch } from "../../state/GlobalStateProvider";
import { ensureSuccess, handleError } from "../../util/ErrorHandling";
import { CenteredContent } from "../../components/CenteredContent";
import { DsarRequest } from "../../dto/DsarRequest";
import { useTurnstile } from "../../util/TurnstileEnv";

const useStyles = makeStyles({
    container: {
        ...flexColumn,
        width: "100%"
    },
    formStyle,
    intro
});

export const DsarForm: React.FC = React.memo(() => {
    const styles = useStyles();
    const globalDispatch = useGlobalDispatch();
    const navigate = useNavigate();
    const { siteKey } = useTurnstile();
    
    const [token, setToken] = React.useState<string>();
    const [request, setRequest] = React.useState<DsarRequest>({ submitAs: "The person, or the parent / guardian of the person, whose name appears above." });
    const [success, setSuccess] = React.useState(false);
    const [inputLocked, setInputLocked] = React.useState(false);

    const navigateToHome = React.useCallback(() =>
    {
        navigate(`/`);
    }, [navigate]);

    const submit = React.useCallback(async () =>
    {
        try
        {
            setInputLocked( true );

            const response = await fetch("/api/dsar", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({...request, token})
            });
            
            await ensureSuccess(response);

            setSuccess(true);
        }
        catch (e)
        {
            handleError(e as Error, globalDispatch);
        }
        finally
        {
            setInputLocked(false);
        }
    }, [globalDispatch, request, token]);

    const options: Record<string, React.ReactElement> = {
        "GDPR": <>
            <Radio value="Confirm that my personal information is being processed" label="Confirm that my personal information is being processed" />
            <Radio value="Access my personal information" label="Access my personal information" />
            <Radio value="Edit / correct my personal information" label="Edit / correct my personal information" />
            <Radio value="Have my personal information deleted" label="Have my personal information deleted" />
            <Radio value="Restrict the processing of my personal information" label="Restrict the processing of my personal information" />
            <Radio value="Ask a question about Ianus Guard's privacy policy" label="Ask a question about Ianus Guard's privacy policy" />
            <Radio value="Withdraw my consent to the processing of my personal information" label="Withdraw my consent to the processing of my personal information" />
            <Radio value="Other (please specify in the comment box below)" label="Other (please specify in the comment box below)" />
        </>,
        "CCPA": <>
            <Radio value="Know what information is being collected from me" label="Know what information is being collected from me" />
            <Radio value="Have my information deleted" label="Have my information deleted" />
            <Radio value="Opt out of having my data sold to third parties" label="Opt out of having my data sold to third parties" />
            <Radio value="Opt in to the sale of my personal data" label="Opt in to the sale of my personal data" />
            <Radio value="Access my personal information" label="Access my personal information" />
            <Radio value="Fix inaccurate information" label="Fix inaccurate information" />
            <Radio value="Receive a copy of my personal information" label="Receive a copy of my personal information" />
            <Radio value="Opt out of having my data shared for cross-context behavioral advertising" label="Opt out of having my data shared for cross-context behavioral advertising" />
            <Radio value="Limit the use and disclosure of my sensitive personal information" label="Limit the use and disclosure of my sensitive personal information" />
            <Radio value="Other (please specify in the comment box below)" label="Other (please specify in the comment box below)" />
        </>,
        "CPA": <>
            <Radio value="Know what information is being collected from me" label="Know what information is being collected from me" />
            <Radio value="Have my personal information deleted" label="Have my personal information deleted" />
            <Radio value="Opt out of having my data sold to third parties" label="Opt out of having my data sold to third parties" />
            <Radio value="Opt out of data processing for targeted advertising" label="Opt out of data processing for targeted advertising" />
            <Radio value="Opt out of data processing for profiling" label="Opt out of data processing for profiling" />
            <Radio value="Access my personal information" label="Access my personal information" />
            <Radio value="Fix inaccurate information" label="Fix inaccurate information" />
            <Radio value="Appeal a data request decision" label="Appeal a data request decision" />
            <Radio value="Other (please specify in the comment box below)" label="Other (please specify in the comment box below)" />
        </>,
        "CTDPA": <>
            <Radio value="Know what information is being collected from me" label="Know what information is being collected from me" />
            <Radio value="Have my personal information deleted" label="Have my personal information deleted" />
            <Radio value="Opt out of having my data sold to third parties" label="Opt out of having my data sold to third parties" />
            <Radio value="Opt out of data processing for targeted advertising" label="Opt out of data processing for targeted advertising" />
            <Radio value="Opt out of data processing for profiling" label="Opt out of data processing for profiling" />
            <Radio value="Access my personal information" label="Access my personal information" />
            <Radio value="Fix inaccurate information" label="Fix inaccurate information" />
            <Radio value="Appeal a data request decision" label="Appeal a data request decision" />
            <Radio value="Other (please specify in the comment box below)" label="Other (please specify in the comment box below)" />
        </>,
        "UCPA": <>
            <Radio value="Know what information is being collected from me" label="Know what information is being collected from me" />
            <Radio value="Have my personal information deleted" label="Have my personal information deleted" />
            <Radio value="Opt out of having my data sold to third parties" label="Opt out of having my data sold to third parties" />
            <Radio value="Opt out of data processing for targeted advertising" label="Opt out of data processing for targeted advertising" />
            <Radio value="Access my personal information" label="Access my personal information" />
            <Radio value="Other (please specify in the comment box below)" label="Other (please specify in the comment box below)" />
        </>,
        "VCDPA": <>
            <Radio value="Know what information is being collected from me" label="Know what information is being collected from me" />
            <Radio value="Have my personal information deleted" label="Have my personal information deleted" />
            <Radio value="Opt out of having my data sold to third parties" label="Opt out of having my data sold to third parties" />
            <Radio value="Opt out of data processing for targeted advertising" label="Opt out of data processing for targeted advertising" />
            <Radio value="Opt out of data processing for profiling" label="Opt out of data processing for profiling" />
            <Radio value="Access my personal information" label="Access my personal information" />
            <Radio value="Fix inaccurate information" label="Fix inaccurate information" />
            <Radio value="Appeal a data request decision" label="Appeal a data request decision" />
            <Radio value="Other (please specify in the comment box below)" label="Other (please specify in the comment box below)" />
        </>,
        "default": <></>
    }

    if (success)
    {
        return (
            <>
                <div className={styles.container}>
                    <p>
                        <CenteredContent>
                            <Text className={styles.intro}>
                                Success!
                            </Text>
                        </CenteredContent>
                    </p>

                    <CenteredContent>
                        <Text>Your data subject access request was received successfully!</Text>
                    </CenteredContent>

                    <CenteredContent>
                        <Button onClick={navigateToHome} appearance="primary">Return to home</Button>
                    </CenteredContent>
                </div>
            </>
        );
    }
    else
    {
        return (
            <>
                <div className={styles.container}>
                    <p>
                        <CenteredContent>
                            <Text className={styles.intro}>
                                Data Subject Access Request Form
                            </Text>
                        </CenteredContent>
                    </p>

                    <div className={styles.formStyle}>
                        <Field label="Website">
                            <Input disabled defaultValue="Ianus Guard" />
                        </Field>

                        <Field label="Your Name" required hint={`${request?.name?.length ?? 0} / 200`}>
                            <Input disabled={inputLocked} maxLength={200} value={request?.name ?? ""} onChange={(e, data) => setRequest((req) => ({...req, name: data.value}))} />
                        </Field>

                        <Field label="What email address do you use to access the above website / app?" required hint={`${request?.emailAddress?.length ?? 0} / 254`}>
                            <Input disabled={inputLocked} maxLength={254} value={request?.emailAddress ?? ""} onChange={(e, data) => setRequest((req) => ({...req, emailAddress: data.value}))} />
                        </Field>

                        <Field label="You are submitting this request as" required>
                            <RadioGroup value={request?.submitAs ?? ""} onChange={(_, data) => setRequest((req) => ({...req, submitAs: data.value }))}>
                                <Radio value="The person, or the parent / guardian of the person, whose name appears above." label="The person, or the parent / guardian of the person, whose name appears above." />
                                <Radio value="An agent authorized by the consumer to make this request on their behalf." label="An agent authorized by the consumer to make this request on their behalf." />
                            </RadioGroup>
                        </Field>

                        <Field label="Under the rights of which law are you making this request?" required>
                            <Combobox value={request?.lawType ?? ""} selectedOptions={request?.lawType ? [request.lawType] : []} onOptionSelect={(e, data) => setRequest((req) => ({...req, lawType: data.optionValue, subject: undefined}))}>
                                <Option key="GDPR" text="GDPR" value="GDPR">GDPR</Option>
                                <Option key="CCPA" text="CCPA" value="CCPA">CCPA</Option>
                                <Option key="CPA" text="CPA" value="CPA">CPA</Option>
                                <Option key="CTDPA" text="CTDPA" value="CTDPA">CTDPA</Option>
                                <Option key="UCPA" text="UCPA" value="UCPA">UCPA</Option>
                                <Option key="VCDPA" text="VCDPA" value="VCDPA">VCDPA</Option>
                                <Option key="OTHER" text="OTHER" value="OTHER">OTHER</Option>
                            </Combobox>
                        </Field>

                        { !!request?.lawType && (
                            <Field label="I am submitting a request to ___________" required>
                                <RadioGroup value={request?.subject ?? ""} onChange={(_, data) => setRequest((req) => ({...req, subject: data.value }))}>
                                    {options[request?.lawType] ?? options["default"]}
                                </RadioGroup>
                            </Field>
                        ) }

                        <Field label="Please leave details regarding your action request or question." hint={`${request?.details?.length ?? 0} / 2000`}>
                            <Textarea disabled={inputLocked} maxLength={2000} value={request?.details ?? ""} onChange={(e, data) => setRequest((req) => ({...req, details: data.value}))} />
                        </Field>

                        <Field label="I confirm that" required />
                        <Checkbox checked={request?.confirmInformation ?? false} label="Under penalty of perjury, I declare all the above information to be true and accurate." onChange={(e, data) => setRequest((req) => ({...req, confirmInformation: data.checked as boolean}))} />
                        <Checkbox checked={request?.confirmConsequences ?? false} label="I understand that the deletion or restriction of my personal data is irreversible and may result in the termination of services with Ianus Guard." onChange={(e, data) => setRequest((req) => ({...req, confirmConsequences: data.checked as boolean}))} />
                        <Checkbox checked={request?.confirmValidationCommunication ?? false} label="I understand that I will be required to validate my request by email, and I may be contacted in order to complete the request." onChange={(e, data) => setRequest((req) => ({...req, confirmValidationCommunication: data.checked as boolean}))} />

                        <Turnstile siteKey={siteKey} onSuccess={setToken} />
                    </div>

                    <CenteredContent>
                        <Button onClick={submit} disabled={inputLocked || !token || !request?.name || !request?.emailAddress || !request?.submitAs || !request?.lawType || !request.subject || !request?.confirmInformation || !request?.confirmConsequences || !request?.confirmValidationCommunication} appearance="primary">Submit</Button>
                    </CenteredContent>
                </div>
            </>
        );
    }
});